/**
 * ============================================================================
 * MOZIGO Personalizer — Production Refactor v2.2 (2026)
 * Asset filename: pb-personalizer.js
 * ============================================================================
 *
 * v2.2 — Debug Mode Properly Wired:
 *   ✓ _logDebugInfo now actually called from save() (was dead code in v2.1)
 *   ✓ _renderDebugPanel shows visible overlay (works inside Theme Editor)
 *   ✓ Toggle via localStorage('mozigo_debug') OR URL ?mozigo_debug=1
 *
 * Compatibility: Fabric.js v5.x
 * ============================================================================
 */

(function () {
  'use strict';

  // ============================================================
  // CONSTANTS
  // ============================================================
  const SELECTORS = {
    root:          '.pb-personalizer-root',
    wrapper:       '.mozigo-personalizer',
    modal:         '.pb-modal',
    backdrop:      '.pb-modal__backdrop',
    canvas:        '.js-fabric-canvas',
    canvasArea:    '.pb-designer-canvas-area',
    fields:        '.js-input-fields',
    result:        '.pb-preview-result',
    btnOpen:       '.pb-btn-open',
    btnClose:      '.pb-btn-close',
    btnSave:       '.pb-btn-save',
    inputVal:      '.js-input-val',
    base64Input:   '.js-design-base64',
    propFirstName: '#pb-prop-firstname',
    propLastName:  '#pb-prop-lastname'
  };

  const DEFAULTS = {
    fontSize:    25,
    color:       '#000000',
    fontFamily:  'Arial',
    align:       'center',
    maxLength:   14,
    jpegQuality: 0.7,
    canvasWidth: 500
  };

  // ============================================================
  // CONFIG PARSER
  // ============================================================
  const ConfigParser = {
    parse(rawJson) {
      let cfg;
      try {
        cfg = typeof rawJson === 'string'
          ? JSON.parse(rawJson || '{}')
          : (rawJson || {});
      } catch (e) {
        console.error('[MOZIGO] Invalid JSON config:', e);
        return { fonts: [], fields: [], layers: [] };
      }
      return {
        fonts:  Array.isArray(cfg.fonts)  ? cfg.fonts  : [],
        fields: Array.isArray(cfg.fields) ? cfg.fields : [],
        layers: Array.isArray(cfg.layers) ? cfg.layers : []
      };
    },

    normalizeFontReferences(config) {
      const lookup = new Map();

      config.fonts.forEach(font => {
        if (!font.key || !font.url) return;
        const canonical = (font.label || font.key).trim();
        font._registerName = canonical;
        lookup.set(canonical.toLowerCase(), canonical);
        lookup.set(font.key.toLowerCase(), canonical);
        if (font.label) lookup.set(font.label.toLowerCase(), canonical);
      });

      config.layers.forEach(layer => {
        if (!layer.style) layer.style = {};
        const requested = layer.style.fontFamily;
        if (!requested) return;
        const resolved = lookup.get(requested.toLowerCase());
        if (resolved) {
          layer.style.fontFamily = resolved;
        } else if (config.fonts.length > 0) {
          console.warn(
            `[MOZIGO] Font "${requested}" not in fonts list. ` +
            `Available: ${[...lookup.values()].join(', ')}`
          );
        }
      });

      return config;
    }
  };

  // ============================================================
  // FONT LOADER
  // ============================================================
  const FontLoader = {
    async loadAll(fonts) {
      if (!fonts || !fonts.length) return;
      const results = await Promise.allSettled(fonts.map(f => this._loadOne(f)));
      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          console.warn(`[MOZIGO] Font failed: ${fonts[i].key}`, r.reason);
        }
      });
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
    },

    _loadOne(font) {
      if (!font.url || !font._registerName) return Promise.resolve();
      const ff = new FontFace(font._registerName, `url(${font.url})`);
      return ff.load().then(loaded => {
        document.fonts.add(loaded);
        return loaded;
      });
    }
  };

  // ============================================================
  // TEXT TRANSFORMER
  // ============================================================
  const TextTransform = {
    apply(text, transform) {
      if (!text) return '';
      switch (transform) {
        case 'uppercase':  return text.toUpperCase();
        case 'lowercase':  return text.toLowerCase();
        case 'capitalize': return text.replace(/\b\w/g, c => c.toUpperCase());
        default:           return text;
      }
    }
  };

  // ============================================================
  // MAIN CONTROLLER
  // ============================================================
  class PBPersonalizer {
    // Static helper — accessible without instance
    static _isDebugMode() {
      try {
        if (localStorage.getItem('mozigo_debug') === '1') return true;
      } catch (e) { /* localStorage may be blocked */ }
      const params = new URLSearchParams(window.location.search);
      return params.get('mozigo_debug') === '1';
    }

    constructor(root) {
      if (!root) return;
      this.root = root;

      this.config = ConfigParser.normalizeFontReferences(
        ConfigParser.parse(root.dataset.config)
      );

      this.baseImageUrl = root.dataset.baseImage;
      this.formId       = root.dataset.formId;

      this.fCanvas            = null;
      this.textLayersByField  = {};
      this.currentValues      = {};
      this.isSaved            = false;
      this.isInitialized      = false;

      this._cacheDOM();
      this._bindEvents();
      this._interceptAddToCart();

      if (PBPersonalizer._isDebugMode()) {
        console.log('[MOZIGO] Debug mode ACTIVE for instance:', this);
      }
    }

    _cacheDOM() {
      this.modal       = this.root.querySelector(SELECTORS.modal);
      this.canvasEl    = this.root.querySelector(SELECTORS.canvas);
      this.fieldsWrap  = this.root.querySelector(SELECTORS.fields);
      this.resultWrap  = this.root.querySelector(SELECTORS.result);
      this.wrapper     = this.root.closest(SELECTORS.wrapper);

      this.base64Input   = this.wrapper?.querySelector(SELECTORS.base64Input);
      this.propFirstName = document.querySelector(SELECTORS.propFirstName);
      this.propLastName  = document.querySelector(SELECTORS.propLastName);
    }

    _bindEvents() {
      this.root.querySelector(SELECTORS.btnOpen)
        ?.addEventListener('click', () => this.open());
      this.root.querySelector(SELECTORS.btnClose)
        ?.addEventListener('click', () => this.close());
      this.root.querySelector(SELECTORS.backdrop)
        ?.addEventListener('click', () => this.close());
      this.root.querySelector(SELECTORS.btnSave)
        ?.addEventListener('click', () => this.save());

      this._escHandler = (e) => {
        if (e.key === 'Escape' && this.modal && !this.modal.hidden) {
          this.close();
        }
      };
      document.addEventListener('keydown', this._escHandler);
    }

    async open() {
      if (!this.modal) return;
      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      if (!this.isInitialized) {
        await this._initFabric();
        this.isInitialized = true;
      }
    }

    close() {
      if (!this.modal) return;
      // Blur focused element BEFORE hiding (a11y compliance)
      if (this.modal.contains(document.activeElement)) {
        document.activeElement.blur();
      }
      this.modal.hidden = true;
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    async _initFabric() {
      if (typeof fabric === 'undefined') {
        console.error('[MOZIGO] Fabric.js library not found');
        return;
      }

      this.fCanvas = new fabric.Canvas(this.canvasEl, {
        preserveObjectStacking: true,
        allowTouchScrolling:    true
      });

      await FontLoader.loadAll(this.config.fonts);
      await this._loadBackground();
      this._renderDesignerUI();
      this.fCanvas.renderAll();
    }

    _loadBackground() {
      return new Promise((resolve) => {
        if (!this.baseImageUrl) return resolve();

        fabric.Image.fromURL(this.baseImageUrl, (img) => {
          if (!img) {
            console.warn('[MOZIGO] Background image failed to load');
            return resolve();
          }
          const area = this.root.querySelector(SELECTORS.canvasArea);
          const parentWidth = area?.clientWidth || DEFAULTS.canvasWidth;
          const scale = parentWidth / img.width;

          this.fCanvas.setDimensions({
            width:  img.width  * scale,
            height: img.height * scale
          });
          this.fCanvas.setBackgroundImage(
            img,
            this.fCanvas.renderAll.bind(this.fCanvas),
            { scaleX: scale, scaleY: scale, crossOrigin: 'anonymous' }
          );
          resolve();
        }, { crossOrigin: 'anonymous' });
      });
    }

    _renderDesignerUI() {
      if (!this.fieldsWrap || !this.config.fields.length) return;
      this.fieldsWrap.innerHTML = '';

      this.config.fields.forEach(field => {
        this.currentValues[field.key] = '';
        const layerCfgs = this.config.layers.filter(l => l.field === field.key);
        this._buildFieldInput(field);
        this.textLayersByField[field.key] = [];
        layerCfgs.forEach(layerCfg => this._buildFieldLayers(field, layerCfg));
      });
    }

    _buildFieldInput(field) {
      const group = document.createElement('div');
      group.className = 'pb-field-item';

      const label = document.createElement('label');
      label.className = 'pb-label';
      label.textContent = field.label || field.key;
      label.htmlFor = `pb-input-${field.key}`;

      const input = document.createElement('input');
      input.type        = 'text';
      input.id          = `pb-input-${field.key}`;
      input.className   = 'pb-input-text js-input-val';
      input.placeholder = field.placeholder || `Enter ${field.label || 'name'}...`;
      input.maxLength   = field.max || DEFAULTS.maxLength;
      if (field.required) input.required = true;

      group.appendChild(label);
      group.appendChild(input);
      this.fieldsWrap.appendChild(group);

      input.addEventListener('input', (e) => {
        this._handleInput(field, e.target.value);
      });
    }

    _buildFieldLayers(field, layerCfg) {
      if (!layerCfg?.positions?.length) return;

      const style = layerCfg.style || {};
      const initial = TextTransform.apply(field.defaultText || '', style.transform);

      layerCfg.positions.forEach(pos => {
        // Build base config
        const textConfig = {
          // Position
          left:                pos.x * this.fCanvas.width,
          top:                 pos.y * this.fCanvas.height,
          angle:               pos.rotation || 0,
          originX:             'center',
          originY:             'center',
          
          // Font
          fontFamily:          style.fontFamily  || DEFAULTS.fontFamily,
          fontSize:            style.fontSize    || DEFAULTS.fontSize,
          fontWeight:          style.fontWeight  || 'normal',
          fontStyle:           style.fontStyle   || 'normal',
          
          // Fill
          fill:                style.color       || DEFAULTS.color,
          
          // Stroke (NEW — map từ JSON)
          stroke:              style.strokeColor || null,
          strokeWidth:         style.strokeWidth || 0,
          paintFirst:          'stroke',  // Stroke vẽ trước, fill vẽ đè → viền nét hơn
          strokeLineJoin:      'round',   // Bo góc viền cho mượt
          strokeLineCap:       'round',
          
          // Shadow (NEW — support cả string lẫn object)
          shadow:              this._parseShadow(style.shadow),
          
          // Text formatting
          textAlign:           style.align       || DEFAULTS.align,
          charSpacing:         style.charSpacing || 0,
          lineHeight:          style.lineHeight  || 1.16,
          underline:           !!style.underline,
          linethrough:         !!style.linethrough,
          overline:            !!style.overline,
          
          // Controls
          hasControls:         true,
          hasBorders:          true,
          cornerSize:          10,
          transparentCorners:  false,
          lockScalingFlip:     true
        };

        const textObj = new fabric.IText(initial || ' ', textConfig);

        this.fCanvas.add(textObj);
        this.textLayersByField[field.key].push({
          obj:          textObj,
          baseFontSize: style.fontSize || DEFAULTS.fontSize,
          transform:    style.transform,
          maxWidth:     layerCfg.maxWidth
        });
      });
    }

    _parseShadow(shadowCfg) {
      if (!shadowCfg) return null;
      
      // Already a string in Fabric format
      if (typeof shadowCfg === 'string') {
        return shadowCfg;
      }
      
      // Object format → convert to fabric.Shadow instance
      if (typeof shadowCfg === 'object') {
        return new fabric.Shadow({
          color:        shadowCfg.color    || 'rgba(0,0,0,0.3)',
          blur:         shadowCfg.blur     || 0,
          offsetX:      shadowCfg.offsetX  || 0,
          offsetY:      shadowCfg.offsetY  || 0,
          affectStroke: !!shadowCfg.affectStroke,
          nonScaling:   !!shadowCfg.nonScaling
        });
      }
      
      return null;
    }

    _handleInput(field, rawValue) {
      this.currentValues[field.key] = rawValue;
      const layers = this.textLayersByField[field.key] || [];

      layers.forEach(item => {
        const finalText = TextTransform.apply(rawValue, item.transform) || ' ';
        item.obj.set('text', finalText);
        item.obj.set({ scaleX: 1, scaleY: 1 });

        if (item.maxWidth && this.fCanvas) {
          const allowedPx = item.maxWidth * this.fCanvas.width;
          const currentW  = item.obj.width;
          if (currentW > allowedPx) {
            const fitScale = allowedPx / currentW;
            item.obj.set({ scaleX: fitScale, scaleY: fitScale });
          }
        }
        item.obj.setCoords();
      });

      this.fCanvas.renderAll();
    }

    // ============================================================
    // SAVE — Now PROPERLY calls debug methods
    // ============================================================
    save() {
      if (!this.fCanvas) return;

      const missing = this.config.fields
        .filter(f => f.required && !(this.currentValues[f.key] || '').trim())
        .map(f => f.label || f.key);

      if (missing.length) {
        alert(`Please fill in: ${missing.join(', ')}`);
        return;
      }

      this.fCanvas.discardActiveObject();
      this.fCanvas.renderAll();

      const dataUrl = this.fCanvas.toDataURL({
        format:     'jpeg',
        quality:    DEFAULTS.jpegQuality,
        multiplier: 1
      });

      if (this.base64Input) this.base64Input.value = dataUrl;

      // ⬇️⬇️⬇️ DEBUG HOOKS — THE FIX ⬇️⬇️⬇️
      // if (PBPersonalizer._isDebugMode()) {
      //   this._logDebugInfo(dataUrl);
      //   this._renderDebugPanel(dataUrl);
      // }
      // ⬆️⬆️⬆️ END DEBUG HOOKS ⬆️⬆️⬆️

      this._syncCartProperties();
      this._renderPreview(dataUrl);

      this.isSaved = true;
      this.close();
    }

    _syncCartProperties() {
      const fields = this.config.fields;
      const count  = fields.length;

      if (count === 1) {
        const v = (this.currentValues[fields[0].key] || '').trim();
        if (this.propFirstName) {
          this.propFirstName.setAttribute('name', 'properties[Personalized Name]');
          this.propFirstName.value = v;
        }
        if (this.propLastName) this.propLastName.removeAttribute('name');
      } else if (count >= 2) {
        const v1 = (this.currentValues[fields[0].key] || '').trim();
        const v2 = (this.currentValues[fields[1].key] || '').trim();
        if (this.propFirstName) {
          this.propFirstName.setAttribute('name', 'properties[First Name]');
          this.propFirstName.value = v1;
        }
        if (this.propLastName) {
          this.propLastName.setAttribute('name', 'properties[Last Name]');
          this.propLastName.value = v2;
        }
      }
    }

    _renderPreview(dataUrl) {
      if (!this.resultWrap) return;
      this.resultWrap.innerHTML = '';
      const img = document.createElement('img');
      img.src = dataUrl;
      img.alt = 'Personalized design preview';
      img.style.cssText = 'width:180px; display:block; margin-top:5px;';
      this.resultWrap.appendChild(img);
    }

    // ============================================================
    // DEBUG METHODS
    // ============================================================
    // _logDebugInfo(dataUrl) {
    //   const sizeKB = (dataUrl.length / 1024).toFixed(1);
    //   const SHOPIFY_LIMIT_KB = 48;
    //   const fits = sizeKB < SHOPIFY_LIMIT_KB;

    //   console.groupCollapsed(
    //     `%c[MOZIGO Debug] Save event — ${sizeKB}KB ${fits ? '✓' : '✗'}`,
    //     `color: ${fits ? '#16a34a' : '#dc2626'}; font-weight: bold;`
    //   );

    //   console.log('Base64 size:        ', sizeKB, 'KB');
    //   console.log('Shopify limit:      ', SHOPIFY_LIMIT_KB, 'KB per property');
    //   console.log('Status:             ', fits ? '✓ Should fit in cart' : '⚠️ LIKELY REJECTED by Shopify');
    //   console.log('Property name:      ', this.base64Input?.getAttribute('name'));
    //   console.log('Inside cart form:   ',
    //     !!this.base64Input?.closest('form[action*="/cart/add"]') ||
    //     !!document.getElementById(this.base64Input?.getAttribute('form'))
    //   );
    //   console.log('Field values:       ', { ...this.currentValues });
    //   console.log('Canvas dimensions:  ', `${this.fCanvas.width} × ${this.fCanvas.height}px`);

    //   if (!fits) {
    //     console.warn(
    //       '%cRECOMMEND:%c Migrate to upload-to-URL pattern (Cloudinary/VPS) ' +
    //       'before scaling. Current setup will silently fail in production cart.',
    //       'background:#dc2626;color:#fff;padding:2px 6px;border-radius:3px;',
    //       'color:#dc2626'
    //     );
    //   }

    //   console.groupEnd();
    // }

    // _renderDebugPanel(dataUrl) {
    //   const sizeKB = (dataUrl.length / 1024).toFixed(1);
    //   const fits = sizeKB < 48;
    //   const formAttr = this.base64Input?.getAttribute('form');
    //   const inForm = !!this.base64Input?.closest('form[action*="/cart/add"]') ||
    //                  !!document.getElementById(formAttr);

    //   let panel = document.getElementById('mozigo-debug-panel');
    //   if (!panel) {
    //     panel = document.createElement('div');
    //     panel.id = 'mozigo-debug-panel';
    //     panel.style.cssText = `
    //       position: fixed; top: 12px; right: 12px; z-index: 99999;
    //       background: #0d1117; color: #c9d1d9;
    //       font-family: ui-monospace, SFMono-Regular, monospace;
    //       font-size: 11px; line-height: 1.5; padding: 12px 16px;
    //       border: 1px solid #30363d; border-radius: 8px;
    //       box-shadow: 0 8px 24px rgba(0,0,0,0.4); max-width: 320px;
    //     `;
    //     document.body.appendChild(panel);
    //   }

    //   const valuesStr = Object.entries(this.currentValues)
    //     .map(([k, v]) => `${k}: "${v}"`).join('<br>');

    //   panel.innerHTML = `
    //     <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;border-bottom:1px solid #30363d;padding-bottom:6px;">
    //       <strong style="color:#58a6ff;">[MOZIGO Debug]</strong>
    //       <span onclick="this.parentNode.parentNode.remove()" style="cursor:pointer;color:#8b949e;font-size:14px;">✕</span>
    //     </div>
    //     <div>Size: <strong style="color:${fits ? '#3fb950' : '#f85149'}">${sizeKB} KB</strong> / 48 KB limit</div>
    //     <div>Status: ${fits ? '<span style="color:#3fb950">✓ Should fit</span>' : '<span style="color:#f85149">⚠️ LIKELY REJECTED</span>'}</div>
    //     <div>Property: <code style="color:#d2a8ff;">${this.base64Input?.getAttribute('name') || 'NOT FOUND'}</code></div>
    //     <div>Form linked: ${inForm ? '<span style="color:#3fb950">✓ yes</span>' : '<span style="color:#f85149">✗ NO</span>'}</div>
    //     <div>Canvas: ${this.fCanvas.width} × ${this.fCanvas.height}px</div>
    //     <div style="margin-top:6px;padding-top:6px;border-top:1px solid #30363d;color:#8b949e;">
    //       ${valuesStr || '<em>(no input values)</em>'}
    //     </div>
    //     <div style="margin-top:8px;padding:6px 8px;background:#161b22;border-radius:4px;font-size:10px;color:#8b949e;">
    //       Disable: <code>localStorage.removeItem('mozigo_debug')</code><br>
    //       Or remove <code>?mozigo_debug=1</code> from URL
    //     </div>
    //   `;
    // }

    _interceptAddToCart() {
      const form =
        (this.formId && document.getElementById(this.formId)) ||
        document.querySelector('form[action*="/cart/add"]');
      if (!form) return;

      const checkAndBlock = (e) => {
        const wrapper = this.root.closest('.mozigo-personalizer');
        const toggleCb = wrapper && wrapper.querySelector('.mozigo-personalizer__checkbox');
        const toggleIsOn = !toggleCb || toggleCb.checked;
        if (!this.isSaved && toggleIsOn) {
          e.preventDefault();
          e.stopImmediatePropagation();
          alert('Please personalize this product before adding to cart.');
          this.open();
        }
      };

      form.addEventListener('submit', checkAndBlock, true);

      const btn = form.querySelector('[name="add"], button[type="submit"]');
      if (btn) btn.addEventListener('click', checkAndBlock, true);
    }

    destroy() {
      document.removeEventListener('keydown', this._escHandler);
      if (this.fCanvas) {
        this.fCanvas.dispose();
        this.fCanvas = null;
      }
      this.isInitialized = false;
    }
  }

  // ============================================================
  // FETCH INTERCEPTOR — block /cart/add when personalization required
  // ============================================================
  const _pbOrigFetch = window.fetch;
  window.fetch = function(url, opts) {
    if (typeof url === 'string' && url.includes('/cart/add')) {
      const root = document.querySelector(SELECTORS.root);
      if (root && root._pbInstance && !root._pbInstance.isSaved) {
        const wrapper = root.closest('.mozigo-personalizer');
        const toggleCb = wrapper && wrapper.querySelector('.mozigo-personalizer__checkbox');
        const toggleIsOn = !toggleCb || toggleCb.checked;
        if (toggleIsOn) {
          alert('Please personalize this product before adding to cart.');
          root._pbInstance.open();
          return Promise.reject(new Error('Personalization required'));
        }
      }
    }
    return _pbOrigFetch.apply(this, arguments);
  };

  // ============================================================
  // BOOTSTRAP
  // ============================================================
  function init() {
    document.querySelectorAll(SELECTORS.root).forEach(el => {
      if (!el._pbInstance) {
        el._pbInstance = new PBPersonalizer(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

  // Re-init when quick-buy modal injects new content into DOM
  const _pbObserver = new MutationObserver(function (mutations) {
    let found = false;
    mutations.forEach(function (m) {
      m.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches(SELECTORS.root)) found = true;
        else if (node.querySelector && node.querySelector(SELECTORS.root)) found = true;
      });
    });
    if (found) init();
  });
  _pbObserver.observe(document.body, { childList: true, subtree: true });

  window.PBPersonalizer = PBPersonalizer;
})();